import { convexQuery, useConvex, useConvexMutation } from "@convex-dev/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQueries, useQuery as useTanStackQuery } from "@tanstack/react-query";
import {
  type FunctionArgs,
  type FunctionReference,
  type FunctionReturnType,
  getFunctionName,
  type PaginationOptions,
  type PaginationResult,
} from "convex/server";
import { ConvexError, convexToJson, type Value } from "convex/values";
import { useEffect, useMemo, useRef, useState } from "react";

type EmptyObject = Record<string, never>;

type ConvexQueryArgsOrSkip<FuncRef extends FunctionReference<"query">> =
  keyof FunctionArgs<FuncRef> extends never
    ? [args?: EmptyObject | "skip"]
    : EmptyObject extends FunctionArgs<FuncRef>
      ? [args?: FunctionArgs<FuncRef> | "skip"]
      : [args: FunctionArgs<FuncRef> | "skip"];

export function useQuery<ConvexQueryReference extends FunctionReference<"query">>(
  funcRef: ConvexQueryReference,
  ...argsOrSkip: ConvexQueryArgsOrSkip<ConvexQueryReference>
): FunctionReturnType<ConvexQueryReference> | undefined {
  const queryOptions =
    argsOrSkip.length === 0
      ? (convexQuery as (...args: Array<unknown>) => unknown)(funcRef)
      : (convexQuery as (...args: Array<unknown>) => unknown)(funcRef, argsOrSkip[0]);

  const { data, error } = useTanStackQuery<FunctionReturnType<ConvexQueryReference>>(
    queryOptions as never,
  );

  if (error) {
    throw error;
  }

  return data;
}

type PaginatedQueryReference = FunctionReference<
  "query",
  "public",
  { paginationOpts: PaginationOptions },
  PaginationResult<unknown>
>;

type PaginatedQueryArgs<Query extends PaginatedQueryReference> = Omit<
  FunctionArgs<Query>,
  "paginationOpts"
>;

type PaginatedQueryItem<Query extends PaginatedQueryReference> =
  FunctionReturnType<Query>["page"][number];

type UsePaginatedQueryReturnType<Query extends PaginatedQueryReference> = {
  results: Array<PaginatedQueryItem<Query>>;
  loadMore: (numItems: number) => void;
} & (
  | {
      status: "LoadingFirstPage";
      isLoading: true;
    }
  | {
      status: "CanLoadMore";
      isLoading: false;
    }
  | {
      status: "LoadingMore";
      isLoading: true;
    }
  | {
      status: "Exhausted";
      isLoading: false;
    }
);

type QueryPageKey = number;

type UsePaginatedQueryState<Query extends PaginatedQueryReference> = {
  query: Query;
  args: PaginatedQueryArgs<Query>;
  id: number;
  nextPageKey: QueryPageKey;
  pageKeys: QueryPageKey[];
  queries: Record<QueryPageKey, PaginatedQueryArgs<Query> & { paginationOpts: PaginationOptions }>;
  ongoingSplits: Record<QueryPageKey, [QueryPageKey, QueryPageKey]>;
  skip: boolean;
};

type PaginationQueryResult<Query extends PaginatedQueryReference> = UseQueryResult<
  FunctionReturnType<Query>,
  Error
>;

let paginationId = 0;

function nextPaginationId() {
  paginationId += 1;
  return paginationId;
}

function serializeConvexValue(value: Value) {
  return JSON.stringify(convexToJson(value));
}

function getPaginatedQueryKey(query: PaginatedQueryReference) {
  const namedQuery = query as unknown as { _name?: unknown };
  if (typeof namedQuery._name === "string") {
    return namedQuery._name;
  }

  const resolvedName = getFunctionName(query);
  return typeof resolvedName === "string"
    ? resolvedName
    : JSON.stringify(convexToJson(resolvedName));
}

function createPaginationState<Query extends PaginatedQueryReference>(
  query: Query,
  args: PaginatedQueryArgs<Query>,
  skip: boolean,
  initialNumItems: number,
): UsePaginatedQueryState<Query> {
  const id = nextPaginationId();

  return {
    query,
    args,
    id,
    nextPageKey: 1,
    pageKeys: skip ? [] : [0],
    queries: skip
      ? {}
      : {
          0: {
            ...args,
            paginationOpts: {
              numItems: initialNumItems,
              cursor: null,
              id,
            },
          },
        },
    ongoingSplits: {},
    skip,
  };
}

function splitQuery<Query extends PaginatedQueryReference>(
  prevState: UsePaginatedQueryState<Query>,
  key: QueryPageKey,
  splitCursor: string,
  continueCursor: string,
): UsePaginatedQueryState<Query> {
  const baseQuery = prevState.queries[key];
  if (!baseQuery) {
    return prevState;
  }

  const splitKey1 = prevState.nextPageKey;
  const splitKey2 = prevState.nextPageKey + 1;

  return {
    ...prevState,
    nextPageKey: prevState.nextPageKey + 2,
    queries: {
      ...prevState.queries,
      [splitKey1]: {
        ...prevState.args,
        paginationOpts: {
          ...baseQuery.paginationOpts,
          endCursor: splitCursor,
        },
      },
      [splitKey2]: {
        ...prevState.args,
        paginationOpts: {
          ...baseQuery.paginationOpts,
          cursor: splitCursor,
          endCursor: continueCursor,
        },
      },
    },
    ongoingSplits: {
      ...prevState.ongoingSplits,
      [key]: [splitKey1, splitKey2],
    },
  };
}

function completeSplitQuery<Query extends PaginatedQueryReference>(
  prevState: UsePaginatedQueryState<Query>,
  key: QueryPageKey,
): UsePaginatedQueryState<Query> {
  const completedSplit = prevState.ongoingSplits[key];
  if (!completedSplit) {
    return prevState;
  }

  const { [key]: _removedQuery, ...queries } = prevState.queries;
  const { [key]: _removedSplit, ...ongoingSplits } = prevState.ongoingSplits;
  const pageIndex = prevState.pageKeys.indexOf(key);
  const pageKeys =
    pageIndex >= 0
      ? [
          ...prevState.pageKeys.slice(0, pageIndex),
          ...completedSplit,
          ...prevState.pageKeys.slice(pageIndex + 1),
        ]
      : prevState.pageKeys;

  return {
    ...prevState,
    queries,
    ongoingSplits,
    pageKeys,
  };
}

function isInvalidCursorError(error: Error) {
  return (
    error.message.includes("InvalidCursor") ||
    (error instanceof ConvexError &&
      typeof error.data === "object" &&
      error.data !== null &&
      "isConvexSystemError" in error.data &&
      error.data.isConvexSystemError === true &&
      "paginationError" in error.data &&
      error.data.paginationError === "InvalidCursor")
  );
}

export function usePaginatedQuery<Query extends PaginatedQueryReference>(
  query: Query,
  args: PaginatedQueryArgs<Query> | "skip",
  options: { initialNumItems: number },
): UsePaginatedQueryReturnType<Query> {
  if (typeof options.initialNumItems !== "number" || options.initialNumItems < 0) {
    throw new Error(
      `\`options.initialNumItems\` must be a positive number. Received \`${options.initialNumItems}\`.`,
    );
  }

  const convexClient = useConvex();
  const skip = args === "skip";
  const rawArgsObject = skip ? ({} as PaginatedQueryArgs<Query>) : args;
  const serializedArgs = serializeConvexValue(rawArgsObject as Value);
  const argsObject = useMemo(() => rawArgsObject, [serializedArgs, skip]);
  const queryKey = getPaginatedQueryKey(query);

  const createInitialState = useMemo(
    () => () => createPaginationState(query, argsObject, skip, options.initialNumItems),
    [argsObject, options.initialNumItems, query, skip],
  );

  const [state, setState] = useState<UsePaginatedQueryState<Query>>(createInitialState);
  const resetKey = `${queryKey}|${serializedArgs}|${skip ? "1" : "0"}|${options.initialNumItems}`;
  const resetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (resetKeyRef.current === resetKey) {
      return;
    }
    resetKeyRef.current = resetKey;
    setState(createInitialState());
  }, [createInitialState, resetKey]);

  const currentState = state;

  const queryKeys = useMemo(
    () =>
      Object.keys(currentState.queries)
        .map(Number)
        .sort((a, b) => a - b),
    [currentState.queries],
  );

  const pageResults = useQueries({
    queries: queryKeys.map((pageKey) => {
      const pageArgs = currentState.queries[pageKey];
      return (convexQuery as (...args: Array<unknown>) => Record<string, unknown>)(query, pageArgs);
    }) as never,
    combine: (results) => results,
  }) as Array<PaginationQueryResult<Query>>;

  const pageResultsByKey = useMemo(() => {
    return new Map(queryKeys.map((pageKey, index) => [pageKey, pageResults[index]]));
  }, [pageResults, queryKeys]);

  const analysis = useMemo(() => {
    let lastPageResult: FunctionReturnType<Query> | undefined;
    const allItems: Array<PaginatedQueryItem<Query>> = [];
    let nextState: UsePaginatedQueryState<Query> | null = null;
    let needsReset = false;
    let thrownError: Error | null = null;

    for (const pageKey of currentState.pageKeys) {
      const queryResult = pageResultsByKey.get(pageKey);
      const data = queryResult?.data;
      const error = queryResult?.error;

      if (error) {
        if (isInvalidCursorError(error)) {
          convexClient.logger.warn(
            `usePaginatedQuery hit error, resetting pagination state: ${error.message}`,
          );
          needsReset = true;
          lastPageResult = undefined;
          break;
        }
        thrownError = error;
        break;
      }

      if (data === undefined) {
        lastPageResult = undefined;
        break;
      }

      lastPageResult = data;
      const ongoingSplit = currentState.ongoingSplits[pageKey];
      if (ongoingSplit) {
        const firstSplitData = pageResultsByKey.get(ongoingSplit[0])?.data;
        const secondSplitData = pageResultsByKey.get(ongoingSplit[1])?.data;

        if (firstSplitData !== undefined && secondSplitData !== undefined) {
          nextState = completeSplitQuery(nextState ?? currentState, pageKey);
        }
      } else if (
        data.splitCursor &&
        (data.pageStatus === "SplitRecommended" ||
          data.pageStatus === "SplitRequired" ||
          data.page.length > options.initialNumItems * 2)
      ) {
        nextState = splitQuery(
          nextState ?? currentState,
          pageKey,
          data.splitCursor,
          data.continueCursor,
        );
      }

      if (data.pageStatus === "SplitRequired") {
        lastPageResult = undefined;
        break;
      }

      allItems.push(...(data.page as Array<PaginatedQueryItem<Query>>));
    }

    return {
      results: allItems,
      maybeLastResult: lastPageResult,
      nextState,
      needsReset,
      thrownError,
    };
  }, [convexClient.logger, currentState, options.initialNumItems, pageResultsByKey]);

  useEffect(() => {
    if (analysis.needsReset) {
      resetKeyRef.current = resetKey;
      setState(createInitialState());
      return;
    }

    if (analysis.nextState && analysis.nextState !== currentState) {
      setState(analysis.nextState);
    }
  }, [analysis.needsReset, analysis.nextState, createInitialState, currentState, resetKey]);

  if (analysis.thrownError) {
    throw analysis.thrownError;
  }

  const status = useMemo(() => {
    if (analysis.maybeLastResult === undefined) {
      if (currentState.nextPageKey === 1) {
        return {
          status: "LoadingFirstPage" as const,
          isLoading: true as const,
          loadMore: (_numItems: number) => {
            // Intentional noop.
          },
        };
      }

      return {
        status: "LoadingMore" as const,
        isLoading: true as const,
        loadMore: (_numItems: number) => {
          // Intentional noop.
        },
      };
    }

    if (analysis.maybeLastResult.isDone) {
      return {
        status: "Exhausted" as const,
        isLoading: false as const,
        loadMore: (_numItems: number) => {
          // Intentional noop.
        },
      };
    }

    const continueCursor = analysis.maybeLastResult.continueCursor;
    let alreadyLoadingMore = false;

    return {
      status: "CanLoadMore" as const,
      isLoading: false as const,
      loadMore: (numItems: number) => {
        if (alreadyLoadingMore) {
          return;
        }
        alreadyLoadingMore = true;

        setState((prevState) => ({
          ...prevState,
          nextPageKey: prevState.nextPageKey + 1,
          pageKeys: [...prevState.pageKeys, prevState.nextPageKey],
          queries: {
            ...prevState.queries,
            [prevState.nextPageKey]: {
              ...prevState.args,
              paginationOpts: {
                numItems,
                cursor: continueCursor,
                id: prevState.id,
              },
            },
          },
        }));
      },
    };
  }, [analysis.maybeLastResult, currentState.nextPageKey]);

  return {
    results: analysis.results,
    ...status,
  };
}

export const useMutation = useConvexMutation;
